import { useCallback, useSyncExternalStore } from 'react';
import {
  DEFAULT_PAYMENT_ISSUES_FILTER,
  type PaymentIssuesFilter,
} from '@ruta/shared';
import { safeLocalStorage } from '@/lib/safeStorage';

// How the header work-queue "Payment Issues" stage counts and which filter it
// carries to the customers page. Configurable under Preferences → Work queue.
// Shape + default live in @ruta/shared so mobile matches exactly. The badge
// count is that search's `found` — never dashboard `paymentIssues.total`.
export const PAYMENT_ISSUES_FILTER_KEY = 'command_payment_issues_filter';

function loadPaymentIssuesFilter(): PaymentIssuesFilter {
  try {
    const raw = safeLocalStorage.getItem(PAYMENT_ISSUES_FILTER_KEY);
    if (raw) return { ...DEFAULT_PAYMENT_ISSUES_FILTER, ...JSON.parse(raw) };
  } catch {
    // Corrupt value — fall back to defaults.
  }
  return { ...DEFAULT_PAYMENT_ISSUES_FILTER };
}

type Listener = () => void;

let current = loadPaymentIssuesFilter();
const listeners = new Set<Listener>();

function emit() {
  for (const listener of listeners) listener();
}

function getSnapshot(): PaymentIssuesFilter {
  return current;
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function setPaymentIssuesFilter(next: PaymentIssuesFilter) {
  current = next;
  safeLocalStorage.setItem(PAYMENT_ISSUES_FILTER_KEY, JSON.stringify(next));
  emit();
}

/**
 * Shared payment-issues criteria for the header work queue and Preferences.
 * One store so changing Preferences updates the MiniPipeline badge immediately.
 */
export function usePaymentIssuesFilter(): [
  PaymentIssuesFilter,
  (next: PaymentIssuesFilter) => void,
] {
  const value = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const setValue = useCallback((next: PaymentIssuesFilter) => {
    setPaymentIssuesFilter(next);
  }, []);
  return [value, setValue];
}
